import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../shared/Layout';
import PageHeader from '../shared/PageHeader';
import FilterBar from '../../components/settings/FilterBar';
import Icon from '../../components/tables/Icon';
import IconAdd from '../../components/icons/IconAdd';
import ClientsChart from './ClientsChart';
import ClientsStageFunnel from './ClientsStageFunnel';
import ClientCreateModal from './ClientCreateModal';
import language from '../../variables/language';
import '../../css/clients-list.css';

const url = '/api/clients';

const Clients = () => {
   const navigate = useNavigate();
   const [clients, setClients] = useState([]);
   const [stats, setStats] = useState(null);
   const [search, setSearch] = useState('');
   const [refresh, setRefresh] = useState(0);
   const [createOpen, setCreateOpen] = useState(false);

   useEffect(() => {
      axios.get(url, { withCredentials: true })
         .then(response => setClients(Array.isArray(response.data) ? response.data : []))
         .catch(err => { if(err.response && err.response.status === 401) navigate('/login'); });
      axios.get(url + '/stats', { withCredentials: true })
         .then(response => setStats(response.data))
         .catch(() => setStats(null));
   }, [refresh, navigate]);

   const visibleClients = useMemo(() => {
      const query = search.trim().toLowerCase();
      const filtered = query ? clients.filter(c =>
         ((c.firstName || '') + ' ' + (c.lastName || '') + ' ' + (c.email || '')).toLowerCase().includes(query)
      ) : clients;
      return [...filtered].sort((a, b) => (b.id || 0) - (a.id || 0));
   }, [clients, search]);

   const refreshList = () => setRefresh(prev => prev + 1);
   const closeCreate = () => setCreateOpen(false);
   const onCreated = () => { closeCreate(); refreshList(); };

   const lang = language[language.lang];

   return (
      <Layout>
         <PageHeader name={lang.clients}>
            <FilterBar search={{ value: search, onChange: setSearch, placeholder: lang.placeholderSearch }} resultCount={visibleClients.length} />
            <div className='clients-header-actions'>
               <div className='clients-add-btn' onClick={() => setCreateOpen(true)} title={lang.clientsPage.addClient}>
                  <IconAdd />
               </div>
            </div>
         </PageHeader>
         <main className='main-block clients-main-block'>
            <div className='main-content txt-c clients-page'>
               <div className='clients-layout'>

                  <div className='clients-overview-col'>
                     <div className='clients-stats-col'>
                        <div className='conv-progress-stat'>
                           <span className='conv-progress-stat-value'>{stats ? stats.totalCount : clients.length}</span>
                           <span className='conv-progress-stat-label'>{lang.clientsPage.totalClients}</span>
                        </div>
                        <div className='conv-progress-stat'>
                           <span className='conv-progress-stat-value'>{stats ? stats.currentMonthCount : '—'}</span>
                           <span className='conv-progress-stat-label'>{lang.clientsPage.newThisMonth}</span>
                        </div>
                     </div>
                     <div className='clients-chart-card'>
                        <p className='clients-chart-title'>{lang.clientsPage.chartTitle}</p>
                        {stats && Array.isArray(stats.monthly) ? (
                           <ClientsChart monthly={stats.monthly} />
                        ) : (
                           <div className='clients-tags-empty'>{lang['Loading ...']}</div>
                        )}
                     </div>
                     {stats && Array.isArray(stats.byStage) ? (
                        <ClientsStageFunnel byStage={stats.byStage} />
                     ) : ''}
                  </div>

                  <div className='clients-list-col'>
                     <table className='table clients-table'>
                        <thead className='table-header'>
                           <tr className='table-h-tr'>
                              <th className='table-h-th'>{lang.name}</th>
                              <th className='table-h-th'>{lang.stage}</th>
                              <th className='table-h-th'>{lang.price}</th>
                              <th className='table-h-th'>{lang.tags}</th>
                              <th className='table-h-th'></th>
                           </tr>
                        </thead>
                        <tbody className='table-body'>
                           {visibleClients.map(client => (
                              <tr key={client.id} className='table-b-tr clients-row' onClick={() => navigate('/clients/' + client.id)}>
                                 <td className='table-b-td'>
                                    <div className='clients-name-cell' style={{'--avatar-color': (client.stage && client.stage.color) || 'var(--lighttext-color)'}}>
                                       <div className='clients-mini-avatar'>{((client.firstName || '?').charAt(0) + (client.lastName || '').charAt(0)).toUpperCase()}</div>
                                       <span className='clients-name-text'>{client.firstName} {client.lastName}</span>
                                    </div>
                                 </td>
                                 <td className='table-b-td'>
                                    {client.stage ? (
                                       <span className='clients-stage-badge' style={{'--stage-color': client.stage.color}}>{client.stage.name}</span>
                                    ) : ''}
                                 </td>
                                 <td className='table-b-td clients-price-cell'>{client.price || 0}</td>
                                 <td className='table-b-td clients-tags-cell'>
                                    {client.tags && client.tags.length > 0 ? (
                                       <div className='conv-tile-tags'>
                                          {client.tags.map(tag => <span className='conv-tile-tag' key={tag.id}>{tag.name}</span>)}
                                       </div>
                                    ) : <span className='clients-tags-empty'>—</span>}
                                 </td>
                                 <td className='table-b-td clients-actions-cell' onClick={e => e.stopPropagation()}>
                                    <Icon mode='card' url={'/clients/' + client.id} className='icon-edit' classes='icon-dimensions-big' />
                                    <Icon mode='delete' url={url + '/' + client.id} onRefreshHundler={refreshList} className='icon-delete' classes='icon-dimensions-big' />
                                 </td>
                              </tr>
                           ))}
                           {visibleClients.length === 0 ? (
                              <tr className='clients-empty-row'><td colSpan={5}>{lang['Empty list']}</td></tr>
                           ) : ''}
                        </tbody>
                     </table>
                  </div>

               </div>
            </div>
         </main>
         <ClientCreateModal isOpen={createOpen} onClose={closeCreate} onCreated={onCreated} />
      </Layout>
   );
}

export default Clients;
